import * as React from 'react';
import { memo, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { daysArr, periodFakeDay } from './mobileConstants';
import { IMyTimeViewDay, IMyTimeViewTask, IPeriodDay, IProjects } from './mobileTypes';
import { addValueToTotalValue, round2Number } from './mobileUtils';
import { useMobile } from './useMobile';
import { polyfill } from './smoothscroll';
import { createCtx } from '../components/Contexts/createCtx';
import { ApiCalls } from '../api/api';
import { Period, UserRegistrationPeriod, UserReportingPeriodState } from '../api/generated/data-contracts';
import { useAllPeriods } from '../hooks/queries/useAllPeriods';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { format } from '../helpers/date';
import { ReportPeriod } from '../components/Contexts/GridContext/context';

(window as any).__forceSmoothScrollPolyfill__ = true;

// // kick off the polyfill!
polyfill();

export interface IMobileContext {
    allProjects: IProjects[];
    selectedTimePeriod: ReportPeriod;
    setSelectedTimePeriod: React.Dispatch<React.SetStateAction<ReportPeriod>>;
    allTimePeriods: Period[];
    setSelectedTimePeriodByDate: (start: Date) => void;
    timeState: UserReportingPeriodState;
    setTimeState: React.Dispatch<React.SetStateAction<UserReportingPeriodState>>;
    periodComment: string;
    setPeriodComment: React.Dispatch<React.SetStateAction<string>>;
    validationErrors: any[];
    setValidationErrors: React.Dispatch<React.SetStateAction<any[]>>;
    isLoading: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    isFirstOpenAfterProjectsOverview: React.MutableRefObject<boolean>;
    onUpdateSuccessHighlightRow: (taskId: string, dayString: string | null, date: Date | null, workTypeId: string | undefined) => void;
    dateFormat: string;
    isPeriodView: boolean;
    selectedPeriodDays: IPeriodDay[];
    showAsPercent: boolean;
}

const [useMobileCtx, MobileCtxProvider] = createCtx<IMobileContext>();

export const useMobileContext = () => useMobileCtx();

export const MobileContextProvider: React.FC = memo(props => {
    // const uiCtx = useUiContext();

    // const [allTimePeriods, setAllTimePeriods] = useState<Period[]>();
    const [selectedTimePeriod, setSelectedTimePeriod] = useState<ReportPeriod | null>();
    const [allItems, setAllItems] = useState<any>();
    const [allProjects, setAllProjects] = useState<any>([]);
    const [timeState, setTimeState] = useState<UserReportingPeriodState>();
    const [periodComment, setPeriodComment] = useState('');
    const [validationErrors, setValidationErrors] = useState([]);
    const [selectedPeriodDays, setSelectedPeriodDays] = useState<IPeriodDay[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const isFirstOpenAfterProjectsOverview = useRef(true);

    // const validator = useRef(new Validator()).current;

    const selectedDate = useMobile(state => state.selectedDate);

    const {
        initialData: {
            configuration: { gridView, timeEntryRoundingInterval, shortDateFormat, gridInputType },
            aggregatedUser,
        },
    } = useConfigValuesState();

    const isPeriodView = useMemo(() => {
        const newIsPeriodView = gridView === 'period';
        useMobile.setState({ isPeriodView: newIsPeriodView });
        return newIsPeriodView;
    }, [gridView]);

    const dateFormat = useMemo(() => {
        const dformat = shortDateFormat;
        let newDateFormat = 'd. MMMM';
        if (dformat !== undefined && dformat != null && dformat.length >= 2) {
            newDateFormat = dformat;
        }
        useMobile.setState({ dateFormat: newDateFormat, timeLowerLimitConfig: timeEntryRoundingInterval });
        return newDateFormat;
    }, [shortDateFormat, timeEntryRoundingInterval]);

    const showAsPercent = useMemo(() => {
        return gridInputType === 'percent';
    }, [gridInputType]);

    const { data: allTimePeriods } = useAllPeriods();

    useEffect(() => {
        // get init period
        const today = new Date();
        setSelectedTimePeriodByDate(today);
    }, []);

    useEffect(() => {
        if (!allTimePeriods || !selectedDate || selectedTimePeriod === undefined) {
            return;
        }
        // if (!allTimePeriods) return;

        // checks if the new selected date should trigger a new selected period
        const thisTimePeriod = allTimePeriods.find(
            p => selectedDate.getTime() >= new Date(p.startDate).getTime() && selectedDate.getTime() <= new Date(p.endDate).getTime(),
        );
        // if (thisTimePeriod && (new Date(thisTimePeriod.startDate).getDate() !== new Date(selectedTimePeriod.start).getDate() || new Date(thisTimePeriod.startDate).getMonth() !== new Date(selectedTimePeriod.start).getMonth())) {
        if (thisTimePeriod) {
            if (
                !selectedTimePeriod ||
                new Date(thisTimePeriod.startDate).getDate() !== new Date(selectedTimePeriod.startDate).getDate() ||
                new Date(thisTimePeriod.startDate).getMonth() !== new Date(selectedTimePeriod.startDate).getMonth()
            ) {
                useMobile.getState().setIsloading({ active: true, overlay: true });
                setSelectedTimePeriodByDate(getCleanDate(new Date(thisTimePeriod.startDate)));
            }
        } else {
            setSelectedTimePeriod(null);
            setAllProjects([]);
            useMobile.setState({
                myTimeView: [],
                totalValues: { all: 0 },
                allTasks: new Map(),
            });
        }
    }, [selectedDate]);

    useEffect(() => {
        if (!allItems || selectedTimePeriod === undefined) {
            return;
        }

        if (selectedTimePeriod === null) {
            setAllProjects([]);
            useMobile.setState({
                myTimeView: [],
                totalValues: { all: 0 },
                allTasks: new Map(),
            });
            return;
        }

        const newAllTasks = new Map();
        const newAllProjects: any[] = [];
        const newTotalValues = { all: 0 };
        const periodDays = getDatesBetweenDates(
            getCleanDate(new Date(selectedTimePeriod.startDate)),
            getCleanDate(new Date(selectedTimePeriod.endDate)),
            dateFormat,
        );
        const myDaysArr: any = isPeriodView
            ? [
                  {
                      dayName: periodFakeDay,
                      dayDate: selectedTimePeriod.startDate,
                      dateString: periodFakeDay,
                      isExpanded: true,
                      projects: [],
                  },
              ]
            : periodDays;

        setSelectedPeriodDays(
            periodDays.map(day => ({
                dayName: day.dayName,
                dayDate: day.dayDate,
                dateString: day.dateString,
            })),
        );

        const allPinnedTasks = allItems.filter(task => task.pinned);

        const newMyTimeView: IMyTimeViewDay[] = myDaysArr.map(day => {
            const newProjects: any = [];

            allPinnedTasks.forEach(task => {
                const indexOfProject = newProjects.findIndex(project => project.projectId === task.projectId);

                if (indexOfProject === -1) {
                    newProjects.push({
                        projectId: task.projectId,
                        projectName: task.projectName,
                        dayName: day.dayName,
                        dayDate: day.dayDate,
                        dateString: day.dateString,
                        pinned: task.pinned,
                        isExpanded: false,
                        tasks: [
                            {
                                taskKey: `${task.taskId}_${task.workType?.id}`,
                                taskId: task.taskId,
                                taskName: task.taskName,
                                workType: task.workType,
                                projectId: task.projectId,
                                dayName: day.dayName,
                                dayDate: day.dayDate,
                                dateString: day.dateString,
                            },
                        ],
                    });
                } else {
                    newProjects[indexOfProject].tasks.push({
                        taskKey: `${task.taskId}_${task.workType?.id}`,
                        taskId: task.taskId,
                        taskName: task.taskName,
                        workType: task.workType,
                        projectId: task.projectId,
                        dayName: day.dayName,
                        dayDate: day.dayDate,
                        dateString: day.dateString,
                    });
                }
            });

            return {
                ...day,
                projects: newProjects,
            };
        });

        allItems.forEach(item => {
            newAllTasks.set(`${item.taskId}_${item.workType?.id}`, item);

            item.timeEntries.forEach(timeEntry => {
                // creating stuff for the myTimeView
                const dateString = isPeriodView ? periodFakeDay : format(new Date(timeEntry.date), dateFormat);
                const dayName = isPeriodView ? periodFakeDay : daysArr[new Date(timeEntry.date).getDay()];
                const dayIndex = isPeriodView ? 0 : newMyTimeView.findIndex(day => day.dateString === dateString);
                const thisTimeEntries = item.timeEntries.filter(te => te.id === timeEntry.id);
                const indexOfProject = newMyTimeView[dayIndex].projects.findIndex(project => project.projectId === item.projectId);

                // muligvis lave det her til referencer til tasks i allItems, så vi kun skal opdatere i allItems?
                if (indexOfProject === -1) {
                    newMyTimeView[dayIndex].projects.push({
                        projectId: item.projectId,
                        projectName: item.projectName,
                        pinned: item.pinned,
                        isExpanded: false,
                        dateString,
                        dayName,
                        dayDate: timeEntry.date,
                        flagged: timeEntry.flagged,
                        tasks: [
                            {
                                taskKey: `${item.taskId}_${item.workType?.id}`,
                                taskId: item.taskId,
                                taskName: item.taskName,
                                projectId: item.projectId,
                                workType: item.workType,
                                dateString,
                                dayName,
                                dayDate: timeEntry.date,
                            },
                        ],
                    });
                } else {
                    const indexOfTask = newMyTimeView[dayIndex].projects[indexOfProject].tasks.findIndex(
                        task => `${task.taskId}_${task.workType.id}` === `${item.taskId}_${item.workType.id}`,
                    );
                    if (indexOfTask === -1) {
                        newMyTimeView[dayIndex].projects[indexOfProject].tasks.push({
                            taskKey: `${item.taskId}_${item.workType?.id}`,
                            taskId: item.taskId,
                            taskName: item.taskName,
                            projectId: item.projectId,
                            workType: item.workType,
                            dateString,
                            dayName,
                            dayDate: timeEntry.date,
                        });
                    }
                }

                // creating totals/values object
                if (thisTimeEntries?.[0]) {
                    newTotalValues['all'] = addValueToTotalValue(newTotalValues['all'], thisTimeEntries[0].hours);

                    if (!newTotalValues[dateString]) {
                        newTotalValues[dateString] = { value: round2Number(thisTimeEntries[0].hours) };
                    } else {
                        newTotalValues[dateString].value = addValueToTotalValue(newTotalValues[dateString].value, thisTimeEntries[0].hours);
                    }
                    if (!newTotalValues[dateString][item.projectId]) {
                        newTotalValues[dateString][item.projectId] = { value: round2Number(thisTimeEntries[0].hours) };
                    } else {
                        newTotalValues[dateString][item.projectId].value = addValueToTotalValue(
                            newTotalValues[dateString][item.projectId].value,
                            thisTimeEntries[0].hours,
                        );
                    }
                    if (isPeriodView && newTotalValues[dateString][item.projectId][`${item.taskId}_${item.workType?.id}`]) {
                        newTotalValues[dateString][item.projectId][`${item.taskId}_${item.workType?.id}`] = addValueToTotalValue(
                            newTotalValues[dateString][item.projectId][`${item.taskId}_${item.workType?.id}`],
                            thisTimeEntries[0].hours,
                        );
                    } else {
                        newTotalValues[dateString][item.projectId][`${item.taskId}_${item.workType?.id}`] = round2Number(thisTimeEntries[0].hours);
                    }
                }
            });

            // creating allProjects for the projectsOverview / search bar
            const indexOfProject = newAllProjects.findIndex(project => project.projectId === item.projectId);

            if (indexOfProject === -1) {
                newAllProjects.push({
                    projectId: item.projectId,
                    projectName: item.projectName,
                    tasks: [{ ...item }],
                });
            } else {
                const indexOfTask = newAllProjects[indexOfProject].tasks.findIndex(
                    task => `${task.taskId}_${task.workType.id}` === `${item.taskId}_${item.workType.id}`,
                );

                if (indexOfTask === -1) {
                    newAllProjects[indexOfProject].tasks.push({ ...item });
                }
            }
        });

        // sorts the projects & tasks in the myTimeView/TimeOverview
        const sortedMyTimeView = newMyTimeView.map(day => ({
            ...day,
            projects: day.projects
                .map(project => ({
                    ...project,
                    tasks: project.tasks.sort((a: IMyTimeViewTask, b: IMyTimeViewTask) => (a.taskName > b.taskName ? 1 : -1)),
                }))
                .sort((a: any, b: any) => (a.projectName > b.projectName ? 1 : -1)),
        }));

        console.debug('myTimeView', sortedMyTimeView);

        const sortedAllProjects = newAllProjects
            .map(project => ({
                ...project,
                tasks: [...project.tasks.sort((a: UserRegistrationPeriod, b: UserRegistrationPeriod) => (a.taskName > b.taskName ? 1 : -1))],
            }))
            .sort((a: any, b: any) => (a.projectName > b.projectName ? 1 : -1));

        setAllProjects(sortedAllProjects);

        useMobile.setState({
            myTimeView: sortedMyTimeView,
            totalValues: newTotalValues,
            allTasks: newAllTasks,
        });

        if (!selectedDate && !isPeriodView) {
            const todaysDate = getTodaysDate();
            useMobile.getState().setSelectedDate({ newSelectedDate: todaysDate });
        }

        useMobile.getState().setIsloading({ active: false, overlay: false });
    }, [allItems]);

    const setSelectedTimePeriodByDate = (start: Date) => {
        const startOfDay = getCleanDate(start);

        ApiCalls.getUserPeriodByDate({ date: startOfDay.getISOStringMidnight() })
            .then(({ data: result }) => {
                if (result === null) {
                    return;
                }

                const nextPeriodStartDate = new Date(result.reportPeriod.endDate);
                nextPeriodStartDate.setDate(nextPeriodStartDate.getDate() + 1);

                const period: ReportPeriod = {
                    periodId: result.reportPeriod.periodId,
                    name: result.reportPeriod.name,
                    state: result.reportPeriod.state,
                    reportPeriodId: result.reportPeriod.reportPeriodId,
                    startDate: new Date(result.reportPeriod.startDate),
                    endDate: new Date(result.reportPeriod.endDate),
                    deadlineDate: new Date(result.reportPeriod.deadlineDate),
                    isClosed: result.reportPeriod.isClosed,
                    workDays: result.reportPeriod.workDays,
                    nextPeriodStartDate: nextPeriodStartDate,
                };

                setTimeState(period.state);

                setPeriodComment(result.reportPeriod.comment || '');

                setSelectedTimePeriod(period);
                setAllItems(result.timesheetData.rows);
            })
            .catch(err => console.log('error: ', err));
    };

    const onUpdateSuccessHighlightRow = useCallback(
        (taskId: string, dayString: string | null, date: Date | null, workTypeId: string | undefined) => {
            const dateString = dayString || format(new Date(date), dateFormat);
            const taskRow = document.getElementById(`${taskId}_${workTypeId}_${dateString}`);
            if (!taskRow) return;
            if (!taskRow.classList.contains('taskIsUpdated')) {
                taskRow.classList.add('taskIsUpdated');
            } else {
                taskRow.classList.add('restartTaskIsUpdated');
            }
        },
        [dateFormat],
    );

    return (
        <MobileCtxProvider
            value={{
                allProjects,
                selectedTimePeriod,
                setSelectedTimePeriod,
                allTimePeriods,
                setSelectedTimePeriodByDate,
                timeState,
                setTimeState,
                periodComment,
                setPeriodComment,
                validationErrors,
                setValidationErrors,
                isLoading,
                setIsLoading,
                isFirstOpenAfterProjectsOverview,
                onUpdateSuccessHighlightRow,
                dateFormat,
                isPeriodView,
                selectedPeriodDays,
                showAsPercent,
            }}
        >
            {props.children}
        </MobileCtxProvider>
    );
});

const getTodaysDate = () => {
    const today = new Date();
    return getCleanDate(today);
};
export const getCleanDate = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).setUTCTime();
};

const getDatesBetweenDates = (startDate: Date, endDate: Date, dateFormat: string) => {
    const dates = [];

    const theDate = new Date(startDate);
    while (theDate < endDate) {
        const dayName = daysArr[new Date(theDate).getDay()];

        dates.push({
            dayName,
            dayDate: getCleanDate(theDate),
            dateString: format(new Date(theDate), dateFormat),
            isExpanded: getExpandedToday(theDate),
            projects: [],
        });

        theDate.setDate(theDate.getDate() + 1);
    }

    const endDateDayName = daysArr[new Date(endDate).getDay()];
    dates.push({
        dayName: endDateDayName,
        dayDate: getCleanDate(endDate),
        dateString: format(new Date(endDate), dateFormat),
        isExpanded: getExpandedToday(endDate),
        projects: [],
    });

    return dates;
};

const getExpandedToday = (date: Date): boolean => {
    const today = new Date();
    if (new Date(date).getDate() === new Date(today).getDate() && new Date(date).getMonth() === new Date(today).getMonth()) {
        return true;
    }
    return false;
};
